'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useDialogs } from '@/components/dialogs/hooks';
import LobbyPageSkeleton from '@/components/skeletons/LobbyPageSkeleton';
import { CurrencyCode } from '@/helpers/currency';
import { Modals } from '@/helpers/modals';
import type { PageParams } from '@/helpers/types';
import { useNavigation } from '@/hooks/navigation';
import { useGetCommonStore, useModalsStore } from '@/store';

export default function Page({
  params: { currency },
}: PageParams & { params: { currency: string } }) {
  const { navigate } = useNavigation();
  const { modal } = useDialogs();
  const useCommonStore = useGetCommonStore();

  const { user, pendingUser, isAuthenticated, updatePlayerWallet } =
    useCommonStore(
      useShallow((state) => ({
        user: state.user,
        pendingUser: state.pendingUser,
        isAuthenticated: state.isAuthenticated,
        updatePlayerWallet: state.updatePlayerWallet,
      }))
    );

  const open = useModalsStore.use.open();

  const searchParams = useSearchParams();

  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (redirect)
      useCommonStore.setState({ afterDepositRedirectUrl: redirect });
  }, [searchParams]);

  const handleOpenLoginModal = useCallback(() => {
    open(Modals.Login, {
      customOnClose: () => {
        navigate('/');
      },
    });
  }, [open, navigate]);

  useEffect(() => {
    if (pendingUser) return;
    if (!(isAuthenticated && user)) {
      if (modal === Modals.Registration || modal === Modals.Recovery) return;

      return handleOpenLoginModal();
    }

    const isValidCurrency = Object.values(CurrencyCode).includes(
      currency as CurrencyCode
    );

    if (isValidCurrency) {
      updatePlayerWallet(currency, user).then(() => {
        navigate('/?modal=deposit');
      });
    } else {
      navigate('/?modal=deposit');
    }
  }, [
    currency,
    navigate,
    pendingUser,
    isAuthenticated,
    modal,
    user,
    handleOpenLoginModal,
    updatePlayerWallet,
  ]);

  return <LobbyPageSkeleton withTitle />;
}
